import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CurrentBrandScope,
  CurrentPracticeScope,
  DateService,
} from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { Appointment, Brand, LabJob } from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type IBrand,
  type ILab,
  type ILabJob,
  type ILabJobType,
  type IPatient,
  type IPractice,
  type IStaffer,
  LabJobStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
  filterNil,
  filterUndefined,
  guardFilter,
  isINamedDocument,
  isSameRef,
  multiFilter,
  orderBy,
  query$,
  startAt,
  toMoment,
  toTimestamp,
  toNamedDocument,
  undeletedFilter,
  where,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import {
  BehaviorSubject,
  type Observable,
  type OperatorFunction,
  ReplaySubject,
  combineLatest,
  of,
} from 'rxjs';
import {
  concatMap,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { type ILabJobFormData, LabJobFormGroup } from './lab-job.formgroup';

@Component({
  selector: 'pr-lab-job-form',
  templateUrl: './lab-job-form.component.html',
  styleUrls: ['./lab-job-form.component.scss'],
  exportAs: 'prLabJobForm',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  standalone: false,
})
export class LabJobFormComponent {
  private _practitionerManuallyOverridden = false;
  private _lastAppointmentRef?: DocumentReference<IAppointment>;
  labJob$ = new ReplaySubject<WithRef<ILabJob>>(1);
  trackByPatient = TrackByFunctions.ref<WithRef<IPatient>>();
  trackByAppointment = TrackByFunctions.ref<WithRef<IAppointment>>();
  trackByLab = TrackByFunctions.ref<WithRef<ILab>>();
  trackByLabJobType = TrackByFunctions.field<ILabJobType>('name');
  labJobFormGroup = new LabJobFormGroup();
  labs$: Observable<WithRef<ILab>[]>;
  labJobTypes$ = new BehaviorSubject<ILabJobType[]>([]);
  patientAppointments$ = new ReplaySubject<WithRef<IAppointment>[]>(1);
  recentPatientAppointments$ = new BehaviorSubject<WithRef<IAppointment>[]>([]);
  practice$: Observable<WithRef<IPractice>>;
  brand$: Observable<WithRef<IBrand>>;
  isComplete$ = this.labJob$.pipe(map((labJob) => LabJob.complete(labJob)));
  // `startWith(false)` so the due-date field renders in create mode (labJob$ never emits until edit).
  isIssued$ = this.labJob$.pipe(
    map((labJob) => labJob.status === LabJobStatus.Issued),
    startWith(false)
  );
  @Output() submitted = new EventEmitter<ILabJobFormData>();
  @Output() cancelled = new EventEmitter<void>();

  @Input() showActions = true;
  @Input() isEdit = false;

  @Input()
  set labJob(labJob: WithRef<ILabJob>) {
    if (labJob) {
      this.labJob$.next(labJob);
    }
  }

  @Input()
  set lab(lab: WithRef<ILab>) {
    if (lab) {
      this.labJobFormGroup.controls.lab.setValue(lab);
      this.labJobFormGroup.controls.type.setValue(lab.labJobTypes[0]);
    }
  }

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.labJobFormGroup.controls.patient.setValue(toNamedDocument(patient));
    }
  }

  @Input()
  set appointment(appointment: WithRef<IAppointment>) {
    if (appointment) {
      this.labJobFormGroup.controls.appointment.setValue(appointment);
    }
  }

  constructor(
    private _brandScope: CurrentBrandScope,
    private _practiceScope: CurrentPracticeScope,
    public dateService: DateService
  ) {
    this.brand$ = this._brandScope.doc$.pipe(filterUndefined());
    this.labs$ = this.brand$.pipe(switchMap((brand) => Brand.labs$(brand)));
    this.practice$ = this._practiceScope.doc$.pipe(filterUndefined());

    this.labJob$
      .pipe(
        take(1),
        filterUndefined(),
        concatMap((labJob) => this._toLabJobFormData(labJob)),
        takeUntilDestroyed()
      )
      .subscribe((labJob) => {
        this.labJobFormGroup.patchFromLabJob(labJob);
        if (labJob.practitioner) {
          this._practitionerManuallyOverridden = true;
        }
      });

    this.labJobFormGroup.controls.patient.valueChanges
      .pipe(
        this._getPatientAppointments(),
        tap((appointments) => {
          const control = this.labJobFormGroup.controls.appointment;
          if (appointments.length) {
            control.enable();
            return;
          }
          control.disable();
        }),
        takeUntilDestroyed()
      )
      .subscribe((appointments) =>
        this.patientAppointments$.next(appointments)
      );

    this._getRecentPatientAppointments$()
      .pipe(takeUntilDestroyed())
      .subscribe((appointments) =>
        this.recentPatientAppointments$.next(appointments)
      );

    this.labJobFormGroup.controls.practitioner.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((practitioner) => {
        this._practitionerManuallyOverridden = !!practitioner;
      });

    this.labJobFormGroup.controls.lab.valueChanges
      .pipe(
        guardFilter(isINamedDocument),
        switchMap((labDoc) => {
          return this.labs$.pipe(
            map((labs) => labs.filter((lab) => isSameRef(lab, labDoc))),
            map((labs) => labs[0].labJobTypes)
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((types) => {
        this.labJobTypes$.next(types);
        const jobTypeControl = this.labJobFormGroup.controls.type;
        if (!jobTypeControl.value) {
          jobTypeControl.setValue(types[0]);
        }
      });

    this.labJobFormGroup.controls.type.valueChanges
      .pipe(filterNil(), takeUntilDestroyed())
      .subscribe((jobType) => {
        if (this.isEdit) {
          return;
        }
        this.labJobFormGroup.controls.cost.setValue(jobType.cost);
      });

    this.labJobFormGroup.controls.appointment.valueChanges
      .pipe(filterNil(), takeUntilDestroyed())
      .subscribe((appointment) => {
        // Only derive dueDate from the appointment when the user has picked a
        // different appointment. Reselecting the same appointment (or edit-mode
        // rehydration) must not clobber a manually edited due date.
        const previousRef = this._lastAppointmentRef;
        this._lastAppointmentRef = appointment.ref;
        if (previousRef && isSameRef({ ref: previousRef }, appointment)) {
          return;
        }
        if (!appointment.event) {
          return;
        }
        const newDueDate = toMoment(appointment.event.from).subtract(1, 'day');
        if (newDueDate) {
          this.labJobFormGroup.controls.dueDate.setValue(newDueDate);
        }
      });

    combineLatest([
      this.labJobFormGroup.controls.appointment.valueChanges,
      this.recentPatientAppointments$,
    ])
      .pipe(
        filter(() => !this._practitionerManuallyOverridden),
        takeUntilDestroyed()
      )
      .subscribe(([appointment, recentAppointments]) => {
        const practitioner = LabJob.derivePractitioner(
          appointment,
          recentAppointments
        );
        if (!practitioner) {
          return;
        }
        this._setPractitionerAutomatically(practitioner);
      });
  }

  save(): void {
    if (this.labJobFormGroup.invalid) {
      return;
    }
    this.submitted.emit(this.labJobFormGroup.getRawValue());
    this.labJobFormGroup.markAsPristine();
  }

  clearDueDate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.labJobFormGroup.controls.dueDate.reset();
  }

  displayFn(patient?: WithRef<IPatient>): string {
    return patient ? patient.name : '';
  }

  isSelectedAppointment(
    appointment?: WithRef<IAppointment>,
    selectedAppointment?: WithRef<IAppointment>
  ): boolean {
    return isSameRef(appointment, selectedAppointment);
  }

  isSelectedNamedDocument(
    namedDocument: INamedDocument,
    selectedNamedDocument: INamedDocument
  ): boolean {
    try {
      return isSameRef(namedDocument, selectedNamedDocument);
    } catch {
      return namedDocument === selectedNamedDocument;
    }
  }

  isSelectedType(type: ILabJobType, selected: ILabJobType): boolean {
    return type.name === selected.name;
  }

  private _getPatientAppointments(): OperatorFunction<
    INamedDocument<IPatient> | undefined,
    WithRef<IAppointment>[]
  > {
    return switchMap((patient) => {
      if (!patient) {
        return of([]);
      }
      return query$(
        Appointment.col(patient),
        orderBy('event.from'),
        startAt(moment().startOf('day').toDate())
      ).pipe(multiFilter(undeletedFilter));
    });
  }

  private async _toLabJobFormData(
    labJob: WithRef<ILabJob>
  ): Promise<ILabJobFormData> {
    const appointment = labJob.appointment
      ? await Firestore.getDoc(labJob.appointment.ref)
      : undefined;
    const dueDate = labJob.dueDate ? toMoment(labJob.dueDate) : undefined;
    return { ...labJob, appointment, dueDate };
  }

  private _getRecentPatientAppointments$(): Observable<
    WithRef<IAppointment>[]
  > {
    return this.labJobFormGroup.controls.patient.valueChanges.pipe(
      switchMap((patient) => {
        if (!patient) {
          return of([]);
        }
        const sixMonthsAgo = moment().subtract(6, 'months');
        return combineLatest([
          this.practice$,
          Firestore.query$(
            Appointment.col(patient),
            where('event.from', '>=', toTimestamp(sixMonthsAgo))
          ),
        ]).pipe(
          map(([practice, appointments]) =>
            appointments
              .filter(undeletedFilter)
              .filter((appointment) =>
                isSameRef(appointment.practice, practice)
              )
          )
        );
      })
    );
  }

  private _setPractitionerAutomatically(
    practitioner: INamedDocument<IStaffer>
  ): void {
    this.labJobFormGroup.controls.practitioner.setValue(practitioner, {
      emitEvent: false,
    });
    this._practitionerManuallyOverridden = false;
  }
}
